/**
 * Spec 083 — Admin → Page Evaluations → New.
 *
 * Submits a URL to POST /api/v1/brands/:brandId/pages/evaluations and
 * navigates to the resulting evaluation's detail page on 202 (queued) or
 * 200 (cached). Surfaces 400/404 errors inline.
 *
 * Phase 9 Session C gap 083-1.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { Icon, Spinner } from '../../components/ui/Icon.js';
import { Button } from '../../components/ui/Button.js';
import { useBrand } from '../../lib/brand-context.js';
import { useToast } from '../../hooks/useToast.js';
import { EmptyState } from '../../components/EmptyState.js';
import { createEvaluation } from '../../api/pageEvaluations.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';

interface FormValues {
  page_url: string;
  force_refresh: boolean;
}

// Matches the server's validator: http(s) URLs only.
const URL_RE = /^https?:\/\/.+/i;

export function PageEvaluationNewPage() {
  const { selectedBrand, loading: brandsLoading } = useBrand();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { page_url: '', force_refresh: false },
  });
  const [apiError, setApiError] = useState<string | null>(null);

  // Phase 9 Session E.2 (gap XC-R2-1): outer spinner short-circuit so we
  // don't flash "No brand selected" while the brand list is still loading.
  if (brandsLoading) {
    return (
      <div
        data-testid="brands-loading"
        className="hub-page--reading flex flex-col items-center justify-center py-16 text-slate-500"
      >
        <Spinner className="text-violet-600" />
        <p className="mt-3 text-sm">Loading brands…</p>
      </div>
    );
  }

  if (!selectedBrand) {
    return (
      <div
        data-testid="admin-page-evaluation-new"
        className="hub-page--reading space-y-4"
      >
        <RoleProvisioningBanner />
        <Breadcrumbs
          items={[
            { label: 'Observatory', to: '/admin' },
            { label: 'Page evaluations', to: '/admin/evaluations' },
            { label: 'New' },
          ]}
        />
        <EmptyState
          title="No brand selected"
          description="Select a brand before submitting a page for evaluation."
        />
      </div>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setApiError(null);
    try {
      const res = await createEvaluation(selectedBrand.id, {
        page_url: values.page_url.trim(),
        force_refresh: values.force_refresh,
      });
      if (res.cached) {
        addToast(
          'Returning cached result from the last 60 minutes (use force refresh to re-run).',
          'success',
        );
      } else {
        addToast(
          'Evaluation queued — results will appear shortly.',
          'success',
        );
      }
      navigate(`/admin/evaluations/${res.evaluation_id}`);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : String(err));
    }
  });

  return (
    <div
      data-testid="admin-page-evaluation-new"
      className="hub-page--reading space-y-4"
    >
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[
          { label: 'Observatory', to: '/admin' },
          { label: 'Page evaluations', to: '/admin/evaluations' },
          { label: 'New' },
        ]}
      />

      <header>
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-search-alt" className="text-violet-600" />
          New page evaluation
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Submit a page from {selectedBrand.name} to evaluate against the
          active GEO rule set.
        </p>
      </header>

      {/* Phase 9 Session E.1 — cross-link to the sitemap coverage flow. */}
      <div
        data-testid="page-eval-coverage-callout"
        className="flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-900"
      >
        <Icon name="bx-layer" className="shrink-0 text-violet-600" />
        <span>
          Want to evaluate an entire sitemap, not just one page?{' '}
          <Link
            to="/coverage"
            className="font-semibold underline decoration-violet-400 underline-offset-2 hover:text-violet-700"
          >
            Run a sitemap coverage analysis →
          </Link>
        </span>
      </div>

      <form
        onSubmit={onSubmit}
        className="hub-section space-y-4"
      >
        <div className="hub-field">
          <label htmlFor="new-evaluation-url" className="hub-field__label">
            Page URL
          </label>
          <input
            id="new-evaluation-url"
            data-testid="new-evaluation-url"
            type="text"
            placeholder="https://example.com/page"
            autoComplete="off"
            spellCheck={false}
            className="hub-input font-mono"
            {...register('page_url', {
              required: 'Page URL is required',
              validate: (v) =>
                URL_RE.test(v.trim()) || 'Must be an http(s) URL',
            })}
          />
          {errors.page_url && (
            <p className="hub-field__help text-[var(--hub-color-danger)]">
              {errors.page_url.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            id="new-evaluation-force"
            data-testid="new-evaluation-force"
            type="checkbox"
            className="h-4 w-4 rounded-md border-slate-300 text-violet-600 focus:ring-violet-500"
            {...register('force_refresh')}
          />
          <label
            htmlFor="new-evaluation-force"
            className="text-sm text-slate-700"
          >
            Force re-evaluation (bypass 60-min cache)
          </label>
        </div>

        {apiError && (
          <div
            data-testid="new-evaluation-error"
            className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            {apiError}
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="primary" type="submit" data-testid="new-evaluation-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Submit for evaluation'}
          </Button>
        </div>
      </form>
    </div>
  );
}
